import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { MdtDispatch, MdtDispatchStatus } from '../../types';

interface DispatchState {
  dispatches: MdtDispatch[];
  activeDispatch: MdtDispatch | null; // The currently pending dispatch on MDT
}

const initialState: DispatchState = {
  dispatches: [],
  activeDispatch: null,
};

const dispatchSlice = createSlice({
  name: 'dispatch',
  initialState,
  reducers: {
    sendMdtDispatch(state, action: PayloadAction<MdtDispatch>) {
      const existing = state.dispatches.find((d) => d.id === action.payload.id);
      if (!existing) {
        state.dispatches.unshift(action.payload);
      }
      state.activeDispatch = action.payload;
    },
    /** Re-sync a pending dispatch from the server (e.g. after MDT login). */
    syncPendingDispatch(state, action: PayloadAction<MdtDispatch>) {
      const d = action.payload;
      const existing = state.dispatches.find((x) => x.id === d.id);
      if (existing) {
        Object.assign(existing, d);
      } else {
        state.dispatches.unshift(d);
      }
      if (d.status === 'pending') {
        state.activeDispatch = d;
      } else if (state.activeDispatch?.id === d.id) {
        state.activeDispatch = null;
      }
    },
    respondToDispatch(
      state,
      action: PayloadAction<{ id: string; status: MdtDispatchStatus; respondedAt: string }>
    ) {
      const { id, status, respondedAt } = action.payload;
      const dispatch = state.dispatches.find((d) => d.id === id);
      if (dispatch) {
        dispatch.status = status;
        dispatch.respondedAt = respondedAt;
      }
      if (state.activeDispatch?.id === id) {
        if (status === 'accepted') {
          state.activeDispatch = { ...state.activeDispatch, status, respondedAt };
        } else {
          state.activeDispatch = null;
        }
      } else if (status === 'accepted' && !state.activeDispatch) {
        const accepted = state.dispatches.find((d) => d.id === id);
        if (accepted) {
          state.activeDispatch = { ...accepted, status, respondedAt };
        }
      }
    },
    cancelDispatch(state, action: PayloadAction<string>) {
      const dispatch = state.dispatches.find((d) => d.id === action.payload);
      if (dispatch) {
        dispatch.status = 'cancelled';
      }
      if (state.activeDispatch?.id === action.payload) {
        state.activeDispatch = null;
      }
    },
    completeDispatch(state, action: PayloadAction<string>) {
      const dispatch = state.dispatches.find((d) => d.id === action.payload);
      if (dispatch) {
        dispatch.status = 'completed';
      }
      if (state.activeDispatch?.id === action.payload) {
        state.activeDispatch = null;
      }
    },
    clearActiveDispatch(state) {
      state.activeDispatch = null;
    },
    resetMdtDispatchState(state) {
      state.dispatches = [];
      state.activeDispatch = null;
    },
  },
});

export const {
  sendMdtDispatch,
  syncPendingDispatch,
  respondToDispatch,
  cancelDispatch,
  completeDispatch,
  clearActiveDispatch,
  resetMdtDispatchState,
} = dispatchSlice.actions;
export default dispatchSlice.reducer;
